// common between PortfolioPerformance.tsx and portfolio-performance.worker.ts

// import alias don't work in web workers
import { seconds } from "../../../utils/time";
import * as metrics from "@eu.mdotm.core/financial-metrics";

// 2026-04-07 as of today, only these metrics are shown to the user
// as discussed with Giacomo Barbanti https://mdotmworkspace.slack.com/archives/C0AQGJU55LJ/p1775569094947739
export const allowedMetrics = [
	"liveAnnualizedTotalReturn",
	// 2026-04-08 a discrepancy has been detected between the reports and the live metrics derived from
	// the available performance series. calmar ratio is the most sensitive metric showing this discrepancy
	// therefore we are hiding it temporarily
	// "liveCalmarRatio",
	"liveEfficiencyRatio",
	"liveMaxDrawDown",
	"liveTotalReturn",
	"liveVolatility",
] as const;

export type MetricName = keyof typeof metrics;

export const metricsMap: Record<keyof typeof metrics, { label: string; scale: "ratio" | "percentage" }> = {
	liveAnnualizedTotalReturn: {
		label: "Annualized Performance",
		scale: metrics.liveAnnualizedTotalReturn.scale,
	},
	liveBattingAverage: { label: "Batting Average", scale: metrics.liveBattingAverage.scale },
	liveCorrelation: { label: "Correlation", scale: metrics.liveCorrelation.scale },
	liveEfficiencyRatio: { label: "Efficiency Ratio", scale: metrics.liveEfficiencyRatio.scale },
	liveMaxDrawDown: { label: "Max Draw Down", scale: metrics.liveMaxDrawDown.scale },
	liveTotalReturn: { label: "Performance", scale: metrics.liveTotalReturn.scale },
	liveTrackingError: { label: "Tracking Error", scale: metrics.liveTrackingError.scale },
	liveVolatility: { label: "Volatility", scale: metrics.liveVolatility.scale },
	liveAlpha: { label: "Alpha", scale: metrics.liveAlpha.scale },
	liveBeta: { label: "Beta", scale: metrics.liveBeta.scale },
	liveCalmarRatio: { label: "Calmar Ratio", scale: metrics.liveCalmarRatio.scale },
	liveDownsideCaptureRatio: {
		label: "Downside Capture Ratio",
		scale: metrics.liveDownsideCaptureRatio.scale,
	},
	liveUpsideCaptureRatio: { label: "Upside Capture Ratio", scale: metrics.liveUpsideCaptureRatio.scale },
};

export const metricNames = allowedMetrics;

/**
 * Metrics computed by the worker, keyed by metric name then by series uuid. A value is `undefined`
 * when the metric could not be computed for that series. Shared by the metrics table, the panel
 * export snapshot, and the export payload builder.
 */
export type ComputedMetrics = Partial<Record<MetricName, Partial<Record<string, number | undefined>>>>;

export type PerformanceSeriesData = Array<[timestampSec: number, value: number]>;

/** A single series fed to the performance chart and metrics table. */
export type PerformancePanelRow = {
	uuid: string;
	name: string;
	series: PerformanceSeriesData;
	color?: string;
	/**
	 * Optional baseline (pre-edit) series. Never charted — it only feeds the worker-computed
	 * baseline metrics powering the in-table deltas.
	 */
	baselineSeries?: PerformanceSeriesData;
};

/** Per-series visibility map keyed by `PerformancePanelRow.uuid`. */
export type VisibleSeries = Record<string, boolean>;

export type PerformanceSeriesWithBenchmark = Array<{
	uuid: string;
	series: PerformanceSeriesData;
	benchmark?: { series: PerformanceSeriesData };
}>;
export function isWeekendSec(timestampSec: number): boolean {
	const day = new Date(seconds(timestampSec)).getUTCDay();
	return day === 0 || day === 6;
}
export function isWeekendMs(timestampSec: number): boolean {
	const day = new Date(timestampSec).getUTCDay();
	return day === 0 || day === 6;
}

export function skipWeekendBackward(timestampMs: number): number {
	let ts = timestampMs;
	while (isWeekendSec(ts / 1000)) {
		ts -= 86400000;
	}
	return ts;
}

/** Given a min and max dates, this function will roll both of them back to the closest weekday */
export function adjustDateRangeSkippingWeekends(range: { min: number; max: number }): { min: number; max: number } {
	return { min: skipWeekendBackward(range.min), max: skipWeekendBackward(range.max) };
}

/** Returns the [min, max] timestamp range (in ms) that spans all series' first and last non-weekend points. */
export function computeDateRangeWholeSeries(rows: ReadonlyArray<{ series: PerformanceSeriesData }>): {
	min: number;
	max: number;
} {
	let min = Infinity;
	let max = -Infinity;
	for (const { series } of rows) {
		const first = series.find(([ts]) => !isWeekendSec(ts));
		const last = series.findLast(([ts]) => !isWeekendSec(ts));
		if (first) {
			min = Math.min(min, first[0]);
		}
		if (last) {
			max = Math.max(max, last[0]);
		}
	}
	return { min: seconds(min), max: seconds(max) };
}
